import { SerializedConstructor } from '@langchain/core/load/serializable';
import { MinusCircleIcon } from '@langchain/untitled-ui-icons';
import * as RawPopover from '@radix-ui/react-popover';

import { useMemo, useState } from 'react';

import { OPTIONAL_MODEL_SECRETS } from '@/Pages/Playground/Playground.constants';
import { gatherSecrets } from '@/Pages/Playground/components/PlaygroundSecrets.utils';
import { ExpandableErrorAlert } from '@/components/ExpandableErrorAlert';
import { SecretInput } from '@/components/SecretInput';
import { Button, Text, Tooltip } from '@/design-system';
import { EProviderSecretName } from '@/hooks/useHasWorkspaceSecrets';
import { useInstanceFlag } from '@/hooks/useInstanceFlag';
import { InstanceFlags } from '@/hooks/useInstanceFlag';
import { useTenantSecretsMutation } from '@/hooks/useSwr';
import AlertCircleIcon from '@/icons/AlertCircleIcon.svg?react';
import CheckCircleIcon from '@/icons/CheckCircleIcon.svg?react';
import { XOR } from '@/types/schema';

import { useTenantSecretSet } from '../utils/useTenantSecretSet';

export function WorkspaceSecretsForm({
  requiredSecrets,
  secretKeys,
  onSuccess,
  hideInputs,
}: {
  requiredSecrets: string[];
  secretKeys: Set<string>;
  onSuccess?: () => void;
  hideInputs?: boolean;
}) {
  const mutation = useTenantSecretsMutation({
    onSuccess: onSuccess,
  });
  const [values, setValues] = useState<Record<string, string>>({});

  function renderIcon(key: string) {
    if (secretKeys.has(key)) {
      return (
        <Tooltip title="Secret is set">
          <div>
            <CheckCircleIcon className="h-5 w-5 text-success" />
          </div>
        </Tooltip>
      );
    } else if (OPTIONAL_MODEL_SECRETS.includes(key)) {
      return (
        <Tooltip title="Secret is optional">
          <div>
            <MinusCircleIcon className="h-5 w-5 text-quaternary" />
          </div>
        </Tooltip>
      );
    } else {
      return (
        <Tooltip title="No secret set">
          <div>
            <AlertCircleIcon className="h-5 w-5 text-error" />
          </div>
        </Tooltip>
      );
    }
  }

  return (
    <form
      className="peer flex flex-col gap-4 p-4"
      onSubmit={(e) => {
        e.preventDefault();
        e.stopPropagation();

        mutation.trigger({
          json: Object.entries(values)
            .filter(([, value]) => !!value)
            .map(([key, value]) => ({ key, value })),
        });
      }}
    >
      {requiredSecrets.map((key) => {
        return (
          <div key={key} className="flex flex-col gap-2">
            <div className="grid grid-cols-[1fr,auto]">
              <span className="font-mono">{key}</span>
              {renderIcon(key)}
            </div>

            {!hideInputs && (
              <SecretInput
                data-testid={`evaluator-secret-input-${key}`}
                value={values[key] ?? ''}
                placeholder={secretKeys.has(key) ? 'Replace secret...' : ''}
                onChange={(e) => {
                  setValues((values) => ({ ...values, [key]: e.target.value }));
                }}
              />
            )}
            {mutation.error && <ExpandableErrorAlert error={mutation.error} />}
          </div>
        );
      })}

      {!hideInputs ? (
        <Button
          type="submit"
          variant="normal"
          color="primary"
          className="ml-auto"
          size="md"
          loading={mutation.isMutating}
        >
          Save
        </Button>
      ) : (
        <p className="text-sm text-tertiary">
          Update or add secrets{' '}
          <a
            href="/settings/secrets"
            target="_blank"
            rel="noreferer noopener"
            className="text-brand-green-400 hover:underline active:underline"
          >
            here
          </a>
          .
        </p>
      )}
    </form>
  );
}

export function WorkspaceSecretsPopover(props: {
  requiredSecrets: string[];
  secretKeys: Set<string>;
  infoMessage: string;
  onSuccess?: () => void;
}) {
  return (
    <div className="flex max-w-[420px] flex-col rounded-xl border border-secondary bg-popover shadow-lg">
      <div className="flex flex-col gap-1 border-b border-secondary p-4">
        <Text variant="h2">Secrets & API keys</Text>
        <Text color="tertiary">{props.infoMessage}</Text>
      </div>
      <WorkspaceSecretsForm
        requiredSecrets={props.requiredSecrets}
        secretKeys={props.secretKeys}
        onSuccess={props.onSuccess}
      />
    </div>
  );
}

export function EvaluatorSecrets(
  props: {
    infoMessage: string;
    triggerComponent: JSX.Element;
    openSecretsRef: React.MutableRefObject<(() => void) | null>;
    bypassPortal?: boolean;
  } & XOR<
    {
      model: SerializedConstructor | undefined;
    },
    {
      apiKeys: EProviderSecretName[];
    }
  >
) {
  const [open, setOpen] = useState(false);
  const secretKeys = useTenantSecretSet();
  const requiredSecrets = useMemo(() => {
    if (props.model) {
      return gatherSecrets(props.model);
    }
    return props.apiKeys ?? [];
  }, [props.model, props.apiKeys]);

  const bypassAuth = useInstanceFlag(
    InstanceFlags.playground_auth_bypass_enabled
  );

  if (props.openSecretsRef && !bypassAuth) {
    props.openSecretsRef.current = () => setOpen(true);
  }

  const popoverContent = (
    <RawPopover.Content sideOffset={5} className="relative z-10">
      <WorkspaceSecretsPopover
        requiredSecrets={requiredSecrets}
        secretKeys={secretKeys}
        infoMessage={props.infoMessage}
        onSuccess={() => setOpen(false)}
      />
    </RawPopover.Content>
  );

  return (
    <RawPopover.Root open={open} onOpenChange={setOpen}>
      <RawPopover.Trigger asChild>{props.triggerComponent}</RawPopover.Trigger>
      {props.bypassPortal ? (
        popoverContent
      ) : (
        <RawPopover.Portal>{popoverContent}</RawPopover.Portal>
      )}
    </RawPopover.Root>
  );
}
